import {
    AttributeFunctionalType,
    AttributesMetadata,
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
    ShipmentAttributes,
} from '../constants/constants'
import { EntityPreset } from '../services/entityPresetServices'
import { isTimeSeries } from './AttributeUtils'
import {
    isEntityReferenceValue,
    updateValuesForPayload,
} from './EntityAttributesUtils'

/**
 * Attributes every record needs its own value for: the identity, and a
 * Device's Trip Number (its own name). A template never holds them.
 */
const PER_RECORD_ATTRIBUTE_IDS = new Set(['id', 'tripnumber'])

/**
 * Attributes the form fills in from the Trip Number the moment one is chosen
 * (Shipment Name takes the trip's name). They are entered per record, so a
 * template is not required to hold them; one that does is saved as filled in.
 */
export const TRIP_DERIVED_ATTRIBUTE_IDS: string[] = [
    ShipmentAttributes.ShipmentNameAttributeId,
]

/**
 * Types a preset template can never hold: relations, derived values and layout.
 * The device relation is Trip Number, which is always entered per shipment.
 */
const NON_TEMPLATABLE_DATA_TYPES = new Set<string>([
    AttributeType.DEVICE_RELATION,
    AttributeType.CUSTOM_ATTRIBUTE,
    AttributeType.SEPARATOR,
    AttributeType.RULESET,
    AttributeType.RULES_FIELD_TRIGGER,
    AttributeType.SEALINE,
    AttributeType.CREATION_DATETIME,
    AttributeType.SENSOR_SNAPSHOT_READINGS,
])

/** Minimal shape read from a form attribute; the form's own attributes carry more. */
export interface PresetSourceAttribute {
    id: string
    dataType: string
    attributeType?: string
    metadata?: Record<string, boolean>
    hide?: boolean
    disabled?: boolean
    required?: boolean
    value?: unknown
    /** The user typed in this field, so it must not disappear under them. */
    userEdited?: boolean
}

/**
 * Whether an attribute is allowed in a preset template at all, whoever is
 * looking. The server rejects the rest with a 400, so the "Save as Template"
 * form leaves them out up front.
 */
export const isTemplatableAttribute = (
    attribute: PresetSourceAttribute,
): boolean => {
    if (!attribute?.id) {
        return false
    }
    if (
        PER_RECORD_ATTRIBUTE_IDS.has(attribute.id.toLowerCase()) ||
        attribute.id === BUSINESS_RULES_FIELD_ID
    ) {
        return false
    }
    if (
        attribute.attributeType &&
        attribute.attributeType !== AttributeFunctionalType.Static
    ) {
        return false
    }
    if (isTimeSeries(attribute as never)) {
        return false
    }
    if (
        attribute.metadata?.[AttributesMetadata.UNIQUE] ||
        attribute.metadata?.[AttributesMetadata.UNIQUE_GLOBAL]
    ) {
        return false
    }
    return !NON_TEMPLATABLE_DATA_TYPES.has(attribute.dataType?.toLowerCase())
}

const isBlank = (value: unknown): boolean =>
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)

/**
 * Builds the `attributeValues` map for "Save as Template" from the
 * create form. Only what the user filled in is saved, in the payload format the
 * create API takes; entity references are sent as plain ids. Fields the user
 * cannot edit (hidden, view-only) and everything `isTemplatableAttribute` rules
 * out are left out.
 */
export const buildPresetAttributeValues = (
    attributes: PresetSourceAttribute[],
): Record<string, unknown> => {
    const eligible = (attributes ?? []).filter(
        (attribute) =>
            isTemplatableAttribute(attribute) &&
            !attribute.hide &&
            !attribute.disabled,
    )
    const values: Record<string, unknown> = {}
    updateValuesForPayload(eligible).forEach((attribute) => {
        const { id, value } = attribute as {
            id: string
            value: unknown
        }
        if (isBlank(value)) {
            return
        }
        values[id] = isEntityReferenceValue(value) ? value.entityId : value
    })
    return values
}

/** Which of a scope's preset templates the create form should apply. */
export type PresetSelection = 'auto' | 'none' | string

/**
 * Picks the preset template to apply: the user's choice, else the scope's
 * default. `'none'` is an explicit "No Template" and always means legacy
 * defaults. A template the user chose stays applied when they then change the
 * Program or Type, even though that scope does not list it (`chosenFrom`).
 */
export const resolvePreset = (
    options: EntityPreset[],
    selection: PresetSelection,
    chosenFrom: EntityPreset[] = [],
): EntityPreset | null => {
    if (selection === 'none') {
        return null
    }
    const chosen =
        options.find((preset) => preset.id === selection) ??
        chosenFrom.find((preset) => preset.id === selection)
    return chosen ?? options.find((preset) => preset.isDefault) ?? null
}

/**
 * Where a create form's preset templates live: the organisation scope when the
 * user chose "No Program" (their own org), else the program behind the virtual
 * org the Program step holds.
 */
export const getPresetScopeParams = (
    selectedOrgId: string,
    loggedInOrgId: string,
): { programId: string } | { virtualOrgId: string } =>
    selectedOrgId === loggedInOrgId
        ? { programId: 'null' }
        : { virtualOrgId: selectedOrgId }

/**
 * Where a preset template saved from the wizard applies, as the create and
 * update calls take it: the organisation when the user chose "No Program" (their
 * own org), else the virtual org the Program step holds, which the server maps
 * to its program.
 */
export const getPresetScopeBody = (
    selectedOrgId: string,
    loggedInOrgId: string,
): { programId: null } | { virtualOrgId: string } =>
    selectedOrgId === loggedInOrgId
        ? { programId: null }
        : { virtualOrgId: selectedOrgId }

/**
 * A preset template read from the API has entity references expanded for
 * display; the update call wants them as plain ids, so turn them back before
 * sending it.
 */
export const presetValuesToPayload = (
    values: Record<string, unknown>,
): Record<string, unknown> =>
    Object.fromEntries(
        Object.entries(values ?? {}).map(([id, value]) => [
            id,
            isEntityReferenceValue(value) ? value.entityId : value,
        ]),
    )

/**
 * A short, entity-agnostic preview of what a preset template holds, for the
 * manage list, as "Label: Value" pairs: referenced entities first, then plain
 * values, up to `limit`. A value whose attribute name is not known (yet) is
 * shown on its own.
 */
export const summarizePresetValues = (
    values: Record<string, unknown>,
    limit = 3,
    labels: Record<string, string> = {},
): string[] => {
    const entries = Object.entries(values ?? {})
    const describe = ([id, value]: [string, unknown]): string => {
        const text = isEntityReferenceValue(value)
            ? value.uid ?? ''
            : String(value)
        return text.length > 0 && labels[id] ? `${labels[id]}: ${text}` : text
    }
    const references = entries.filter(([, value]) =>
        isEntityReferenceValue(value),
    )
    const plain = entries.filter(
        ([, value]) => typeof value === 'string' || typeof value === 'number',
    )
    return [...references, ...plain]
        .map(describe)
        .filter((text) => text.length > 0)
        .slice(0, limit)
}

/**
 * A preset template's name in the template picker, which lists every scope in
 * one list, so a Program's name and the default flag travel with the name. An
 * organization-wide template is just its name.
 */
export const getPresetOptionLabel = (
    preset: Pick<EntityPreset, 'name' | 'programName' | 'isDefault'>,
    labels: {
        default: string
    },
): string => {
    const scope = preset.programName ? ` - ${preset.programName}` : ''
    const suffix = preset.isDefault ? ` (${labels.default})` : ''
    return `${preset.name}${scope}${suffix}`
}

/**
 * Whether a field is mandatory when authoring a template: required, visible,
 * editable and something a template can hold. Trip Number, Unique fields and
 * the fields filled from the Trip Number (Shipment Name) are entered per
 * record, so a template never asks for them (they stay mandatory on a real
 * record).
 */
export const isRequiredInTemplate = (
    attribute: PresetSourceAttribute,
): boolean =>
    !!attribute.required &&
    !attribute.hide &&
    !attribute.disabled &&
    isTemplatableAttribute(attribute) &&
    !TRIP_DERIVED_ATTRIBUTE_IDS.includes(attribute.id)

/** The mandatory fields a template needs before it can be saved and are still empty. */
export const getMissingRequiredForTemplate = (
    attributes: PresetSourceAttribute[],
): string[] =>
    (attributes ?? [])
        .filter(
            (attribute) =>
                isRequiredInTemplate(attribute) && isBlank(attribute.value),
        )
        .map((attribute) => attribute.id)

/**
 * Whether the template being edited differs from how it was opened, so the
 * Update Template button has something to save: the name changed, a field was
 * edited by the user, or another Program was chosen. A Program not resolved yet
 * (`originalOrgId` empty) is the form still loading, not a change.
 */
export const isPresetEdited = ({
    originalName,
    name,
    originalOrgId,
    orgId,
    attributes,
}: {
    originalName: string
    name: string
    originalOrgId: string
    orgId: string
    attributes: PresetSourceAttribute[] | undefined
}): boolean =>
    name.trim() !== originalName.trim() ||
    (!!originalOrgId && orgId !== originalOrgId) ||
    (attributes ?? []).some((attribute) => !!attribute.userEdited)

/** Orders groups of templates: "No Program" first, then programs by name. */
const comparePresetGroups = (a: EntityPreset[], b: EntityPreset[]): number => {
    if (a[0].programId === null) {
        return -1
    }
    if (b[0].programId === null) {
        return 1
    }
    return (a[0].programName ?? '').localeCompare(b[0].programName ?? '')
}

/**
 * Splits templates into one run per Program, "No Program" first and then each
 * program by name, so templates of different programs are never mixed in a run.
 */
export const groupPresetsByProgram = (
    presets: EntityPreset[],
): EntityPreset[][] =>
    Object.values(
        presets.reduce<Record<string, EntityPreset[]>>((acc, preset) => {
            const key = preset.programId ?? ''
            acc[key] = [...(acc[key] ?? []), preset]
            return acc
        }, {}),
    ).sort(comparePresetGroups)
